import { useCallback, useEffect, useRef, useState } from 'react';
import { useAuth } from 'react-oidc-context';
import { useApiFetch } from '../api';
import type {
  AdminWalletRow,
  AdminWalletShow,
  AdminWalletTransaction,
  AdminWalletTransactionsQuery,
} from '../types/models/Wallet';
import type {
  ApiResponseAdminWalletList,
  ApiResponseAdminWalletShow,
  ApiResponseAdminWalletTransactions,
} from '../types/api/ApiResponseAdminWallet';

// Staff-only hooks over docs/api-types/admin-wallets.md. Mount them behind the `is_ancalagon_staff`
// gate (every admin page self-guards with <NotFound />) so the 403-style "restricted to Ancalagon
// staff" error never fires for customer tenants. All responses are JSend at HTTP 200 — branch on
// `body.status`, never `response.ok`.

interface UseAdminWalletsReturn {
  wallets: AdminWalletRow[];
  /** Echo of the `at` cut-off the balances were computed for (`null` = current balances). */
  at: string | null;
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

/**
 * `GET /admin/wallets[?at=YYYY-MM-DD]` — every wallet with its client. Pass a strict `YYYY-MM-DD`
 * (or `undefined`) as `at`: the balance is then the end-of-day balance at that date (the deposit
 * liability at a cut-off) instead of the current one.
 */
export const useAdminWallets = (at: string | undefined, enabled: boolean = true): UseAdminWalletsReturn => {
  const auth = useAuth();
  const apiFetch = useApiFetch();
  const [wallets, setWallets] = useState<AdminWalletRow[]>([]);
  const [atEcho, setAtEcho] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(enabled);
  const [error, setError] = useState<string | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  const fetchWallets = useCallback(async () => {
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    setLoading(true);
    setError(null);
    try {
      const qs = at ? `?${new URLSearchParams({ at }).toString()}` : '';
      const response = await apiFetch(`/admin/wallets${qs}`, { signal: controller.signal });
      const body = (await response.json().catch(() => null)) as ApiResponseAdminWalletList | null;
      if (body?.status === 'success' && body.data?.wallets) {
        setWallets(body.data.wallets);
        setAtEcho(body.data.at ?? null);
        return;
      }
      // A malformed `at` is a RequestException with the detail under data.at.
      const atError = (body?.data as { at?: unknown } | undefined)?.at;
      setError(
        typeof atError === 'string' ? `Invalid date: ${atError}` : (body?.message ?? 'Could not load the wallets.'),
      );
    } catch (err) {
      if ((err instanceof DOMException && err.name === 'AbortError') || controller.signal.aborted) {
        return;
      }
      setError(err instanceof Error ? err.message : 'An error occurred while loading the wallets.');
    } finally {
      if (abortRef.current === controller) {
        setLoading(false);
      }
    }
  }, [apiFetch, at]);

  useEffect(() => {
    if (enabled && auth.isAuthenticated) {
      void fetchWallets();
    }
  }, [enabled, auth.isAuthenticated, fetchWallets]);

  return { wallets, at: atEcho, loading, error, refetch: fetchWallets };
};

interface UseAdminWalletReturn {
  detail: AdminWalletShow | null;
  loading: boolean;
  error: string | null;
  /** `true` when the backend answered "Organization not found" — render a not-found state, not an error. */
  notFound: boolean;
  refetch: () => Promise<void>;
}

/** `GET /admin/wallets/{clientId}` — one client's wallet, ledger (last 200), top-ups and invoice payments. */
export const useAdminWallet = (clientId: number | null): UseAdminWalletReturn => {
  const auth = useAuth();
  const apiFetch = useApiFetch();
  const [detail, setDetail] = useState<AdminWalletShow | null>(null);
  const [loading, setLoading] = useState<boolean>(clientId !== null);
  const [error, setError] = useState<string | null>(null);
  const [notFound, setNotFound] = useState(false);

  const fetchDetail = useCallback(async () => {
    if (clientId === null) {
      setDetail(null);
      setLoading(false);
      setNotFound(true);
      return;
    }
    setLoading(true);
    setError(null);
    setNotFound(false);
    try {
      const response = await apiFetch(`/admin/wallets/${clientId}`);
      const body = (await response.json().catch(() => null)) as ApiResponseAdminWalletShow | null;
      if (body?.status === 'success' && body.data?.wallet && body.data.client) {
        setDetail({
          client: body.data.client,
          wallet: body.data.wallet,
          transactions: body.data.transactions ?? [],
          deposits: body.data.deposits ?? [],
          invoice_payments: body.data.invoice_payments ?? [],
        });
        return;
      }
      if (body?.message === 'Organization not found') {
        setNotFound(true);
        return;
      }
      setError(body?.message ?? 'Could not load this wallet.');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred while loading this wallet.');
    } finally {
      setLoading(false);
    }
  }, [apiFetch, clientId]);

  useEffect(() => {
    if (auth.isAuthenticated) {
      void fetchDetail();
    }
  }, [auth.isAuthenticated, fetchDetail]);

  return { detail, loading, error, notFound, refetch: fetchDetail };
};

interface UseAdminWalletTransactionsReturn {
  transactions: AdminWalletTransaction[];
  /** `true` when the 5000-row cap was hit — narrow the period. */
  truncated: boolean;
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

/**
 * `GET /admin/wallets/transactions?from&to&client_id&kind` — the cross-client ledger for a period
 * (the accountant's reconciliation export). Empty filter values are omitted from the query string.
 */
export const useAdminWalletTransactions = (
  query: AdminWalletTransactionsQuery,
  enabled: boolean = true,
): UseAdminWalletTransactionsReturn => {
  const auth = useAuth();
  const apiFetch = useApiFetch();
  const [transactions, setTransactions] = useState<AdminWalletTransaction[]>([]);
  const [truncated, setTruncated] = useState(false);
  const [loading, setLoading] = useState<boolean>(enabled);
  const [error, setError] = useState<string | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  const qs = new URLSearchParams();
  if (query.from) qs.set('from', query.from);
  if (query.to) qs.set('to', query.to);
  if (query.client_id !== undefined && query.client_id > 0) qs.set('client_id', String(query.client_id));
  if (query.kind) qs.set('kind', query.kind);
  const paramsKey = qs.toString();

  const fetchTransactions = useCallback(async () => {
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    setLoading(true);
    setError(null);
    try {
      const response = await apiFetch(`/admin/wallets/transactions${paramsKey ? `?${paramsKey}` : ''}`, {
        signal: controller.signal,
      });
      const body = (await response.json().catch(() => null)) as ApiResponseAdminWalletTransactions | null;
      if (body?.status === 'success' && body.data?.transactions) {
        setTransactions(body.data.transactions);
        setTruncated(body.data.truncated === true);
        return;
      }
      setError(body?.message ?? 'Could not load the ledger.');
    } catch (err) {
      if ((err instanceof DOMException && err.name === 'AbortError') || controller.signal.aborted) {
        return;
      }
      setError(err instanceof Error ? err.message : 'An error occurred while loading the ledger.');
    } finally {
      if (abortRef.current === controller) {
        setLoading(false);
      }
    }
  }, [apiFetch, paramsKey]);

  useEffect(() => {
    if (enabled && auth.isAuthenticated) {
      void fetchTransactions();
    }
  }, [enabled, auth.isAuthenticated, fetchTransactions]);

  return { transactions, truncated, loading, error, refetch: fetchTransactions };
};
